import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDistanceToNow, parseISO } from "date-fns";

const TYPE_DOT = {
  kudos_received: "bg-violet-500",
  review_action_required: "bg-amber-500",
  review_signed_off: "bg-emerald-500",
  feedback360_request: "bg-blue-500",
  feedback360_received: "bg-blue-500",
  objective_decision: "bg-rose-500",
  objective_approved: "bg-emerald-500",
  review_due: "bg-amber-500",
  calibration_committed: "bg-slate-700",
  deadline_reminder: "bg-amber-500",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const reload = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 45000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    setLoading(true);
    try {
      await api.post("/notifications/read-all");
      await reload();
    } finally { setLoading(false); }
  };

  const click = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      reload();
    }
    if (n.link_url) navigate(n.link_url);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) reload(); }}>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition" data-testid="notification-bell">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notification-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-display text-sm font-semibold text-slate-900">Notifications</div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={markAll} disabled={loading} className="h-7 px-2 text-xs" data-testid="mark-all-read">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Mark all read</>}
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400" data-testid="notif-empty">
              <Bell className="w-5 h-5 mx-auto text-slate-300 mb-2" />
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => click(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition flex items-start gap-3 ${!n.is_read ? "bg-blue-50/30" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`w-2 h-2 mt-1.5 rounded-full shrink-0 ${TYPE_DOT[n.type] || "bg-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 leading-snug">{n.title}</div>
                      {n.body && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1.5">
                        {n.created_at ? formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }) : ""}
                      </div>
                    </div>
                    {!n.is_read && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 text-center">
          <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="notif-view-all">View all</Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
